import { JSX, useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useVerifyDns } from "../../hooks/useDomain";
import styles from "./DnsVerification.module.css";
import logo from "../../assets/logo.png";

type CheckState = "checking" | "done" | "pending" | "failed";

interface DnsCheckItemProps { label: string; state: CheckState; }

const DnsCheckItem = ({ label, state }: DnsCheckItemProps): JSX.Element => (
  <div className={styles.checkItem}>
    <div className={`${styles.checkDot} ${styles[`checkDot_${state}`]}`}>
      {state === "done" && (<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20,6 9,17 4,12" /></svg>)}
      {state === "checking" && <div className={styles.checkPulse} />}
      {state === "failed" && (<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>)}
    </div>
    <span className={styles.checkLabel}>{label}</span>
  </div>
);

const DnsVerification = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { execute: verifyDns } = useVerifyDns();

  const locState = location.state as { domain?: string; domainId?: string } | null;
  const domain = locState?.domain || "yourdomain.com";
  const domainId = locState?.domainId || "";

  const [attempt, setAttempt] = useState<number>(1);
  const [progress, setProgress] = useState<number>(0);
  const [aRecordState, setARecordState] = useState<CheckState>("checking");
  const [wwwState, setWwwState] = useState<CheckState>("checking");
  const [sslState, setSslState] = useState<CheckState>("pending");
  const [failed, setFailed] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  useEffect(() => { return () => { mounted.current = false; }; }, []);

  const poll = useCallback(async () => {
    if (!domainId || !mounted.current) return;
    const result = await verifyDns(domainId);
    if (!mounted.current) return;

    if (result && result.verified) {
      setARecordState("done");
      setWwwState("done");
      setSslState("checking");
      setProgress(100);
      setTimeout(() => {
        if (mounted.current) navigate("/provisioning", { state: { domain, domainId } });
      }, 1000);
      return;
    }

    setAttempt((a) => a + 1);
    setProgress((p) => Math.min(p + 8, 85));

    if (attempt >= 15) {
      setARecordState("failed");
      setWwwState("failed");
      setFailed(true);
      return;
    }

    pollingRef.current = setTimeout(poll, 4000);
  }, [domainId, verifyDns, navigate, domain, attempt]);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prev) => { if (prev >= 85) return 85; return prev + 1.2; });
    }, 40);

    pollingRef.current = setTimeout(poll, 2000);

    return () => {
      clearInterval(progressInterval);
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [poll]);

  return (
    <div className={styles.page}>
      <div className={styles.blobBlue} />
      <div className={styles.blobGray} />
      <div className={styles.container}>
        <div className={styles.logo}><img src={logo} alt="InstantGDS" className={styles.logoImg} /></div>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.stepBadge}>Step 2 of 3</div>
            <h1 className={styles.title}>{failed ? "DNS Verification Failed" : "Verifying your domain"}</h1>
            <p className={styles.subtitle}>Checking DNS configuration for <span className={styles.domainHighlight}>{domain}</span></p>
          </div>
          {!failed && (
            <div className={styles.statusBox}>
              <div className={styles.scanLine} />
              <div className={styles.statusInner}>
                <div className={styles.spinnerWrapper}><div className={styles.spinnerRing} /><div className={styles.spinnerDot} /></div>
                <div className={styles.statusText}>Checking DNS records...</div>
                <div className={styles.attemptBadge}>Attempt {attempt}</div>
              </div>
            </div>
          )}
          <div className={styles.progressSection}>
            <div className={styles.progressLabelRow}><span className={styles.progressLabel}>DNS propagation check</span><span className={styles.progressPct}>{Math.round(progress)}%</span></div>
            <div className={styles.progressTrack}><div className={styles.progressBar} style={{ width: `${progress}%` }} /></div>
          </div>
          <div className={styles.checklistBox}>
            <div className={styles.checklistTitle}>What we're checking:</div>
            <div className={styles.checklistItems}>
              <DnsCheckItem label="A record → 103.21.58.120" state={aRecordState} />
              <DnsCheckItem label={`www.${domain} → 103.21.58.120`} state={wwwState} />
              <DnsCheckItem label="SSL challenge endpoint reachable" state={sslState} />
            </div>
          </div>
          {failed ? (
            <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
              <button onClick={() => { setFailed(false); setAttempt(1); setProgress(0); setARecordState("checking"); setWwwState("checking"); pollingRef.current = setTimeout(poll, 1000); }} style={{ flex: 1, padding: "12px", background: "#1D4ED8", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>Retry Verification</button>
              <button onClick={() => navigate("/domain-entry")} style={{ flex: 1, padding: "12px", background: "#F1F5F9", color: "#334155", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}>Change Domain</button>
            </div>
          ) : (
            <p className={styles.note}>DNS propagation can take a few minutes to a few hours. This page will automatically continue once verified.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DnsVerification;